import { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@pflegenavi/shared-frontend/platform';
import type {
  BookkeepingChange,
  GetBookkeepingChangesResult,
  GetEarningsChangesResult,
  GetImmediateTransferFeesResult,
  GetReportingOverviewResult,
} from '../reporting-phoenix/api';
import { useReportingPhoenixApi } from '../reporting-phoenix';
import type { CashListConfiguration } from '../cash-management-phoenix/api';
import { useCashManagementPhoenixApi } from '../cash-management-phoenix';

export interface NursingHomeReportingData {
  nursing_home_id: string;
  overview: GetReportingOverviewResult['data'];
  bookkeeping_changes: BookkeepingChange[];
  /** null when the nursing home has no configuration row */
  cash_list_configuration: CashListConfiguration | null;
}

export interface NursingHomesReporting {
  /** Loaded nursing homes, in the order of the requested ids */
  data: NursingHomeReportingData[];
  failedNursingHomeIds: string[];
  settledCount: number;
  isComplete: boolean;
}

/** Only the first unsettled nursing home may fetch, so one request runs at a time. */
export const sequentialEnabled = (settled: boolean[]): boolean[] => {
  const next = settled.indexOf(false);
  return settled.map((_, index) => next === -1 || index <= next);
};

// Legal entities can have a hundred nursing homes; loading them all at once
// overloads the backend, so they load one by one and render as they arrive.
export const useNursingHomesReportingSequentialPhoenix = ({
  nursingHomeIds,
  from,
  to,
  cashListName,
}: {
  nursingHomeIds: string[];
  from: string | undefined;
  to: string | undefined;
  cashListName: string;
}): NursingHomesReporting => {
  const queryKey = (nursingHomeId: string) => [
    'GET_NURSING_HOME_REPORTING',
    nursingHomeId,
    from,
    to,
    cashListName,
  ];
  const reportingApi = useReportingPhoenixApi();
  const cashManagementApi = useCashManagementPhoenixApi();

  const authReady =
    !reportingApi.authContext.initializing &&
    reportingApi.authContext.getTokenImmediate() !== undefined;

  const combine = useCallback(
    (
      results: Array<UseQueryResult<NursingHomeReportingData, unknown>>
    ): NursingHomesReporting => ({
      data: results.flatMap((result) => (result.data ? [result.data] : [])),
      failedNursingHomeIds: nursingHomeIds.filter(
        (_, index) => results[index]?.isError
      ),
      settledCount: results.filter((result) => result.status !== 'pending')
        .length,
      isComplete: results.every((result) => result.status !== 'pending'),
    }),
    [nursingHomeIds]
  );

  // The useQueries below subscribes to every key, so this rerenders on settle.
  const queryClient = useQueryClient();
  const settled = nursingHomeIds.map((nursingHomeId) => {
    const status = queryClient.getQueryState(queryKey(nursingHomeId))?.status;
    return status === 'success' || status === 'error';
  });
  const enabled = sequentialEnabled(settled);

  return useQueries({
    queries: nursingHomeIds.map((nursingHomeId, index) => ({
      queryKey: queryKey(nursingHomeId),
      queryFn: async (): Promise<NursingHomeReportingData> => {
        if (!from || !to) {
          throw new Error('Missing date range');
        }
        // Sequential on purpose: one request in flight at a time.
        const overview = await reportingApi.getOverview({
          params: { nursing_home_id: nursingHomeId, from, to },
        });
        const bookkeepingChanges = await reportingApi.getBookkeepingChanges({
          params: {
            nursing_home_id: nursingHomeId,
            period_start: from,
            period_end: to,
            cash_list_name: cashListName,
          },
        });
        const configuration = await cashManagementApi.getCashListConfiguration({
          params: { nursing_home_id: nursingHomeId },
        });
        const cashListConfiguration: CashListConfiguration | null =
          configuration.data;
        return {
          nursing_home_id: nursingHomeId,
          overview: overview.data,
          bookkeeping_changes: bookkeepingChanges.data,
          cash_list_configuration: cashListConfiguration,
        };
      },
      enabled: authReady && !!from && !!to && (enabled[index] ?? false),
      // Refetching would fire every cached or failed nursing home at once.
      staleTime: Infinity,
      retryOnMount: false,
    })),
    combine,
  });
};

// One request for the whole legal entity, so its total does not wait for every
// nursing home to load.
export const useLegalEntityBookkeepingChangesPhoenix = ({
  legalEntityId,
  from,
  to,
  cashListName,
}: {
  legalEntityId: string | undefined;
  from: string | undefined;
  to: string | undefined;
  cashListName: string;
}): UseQueryResult<GetBookkeepingChangesResult, unknown> =>
  useApiQuery(
    useReportingPhoenixApi,
    [
      'GET_LEGAL_ENTITY_BOOKKEEPING_CHANGES',
      legalEntityId,
      from,
      to,
      cashListName,
    ],
    (api) => {
      if (!legalEntityId || !from || !to) {
        throw new Error('Missing legal entity ID or date range');
      }
      return api.getBookkeepingChanges({
        params: {
          legal_entity_id: legalEntityId,
          period_start: from,
          period_end: to,
          cash_list_name: cashListName,
        },
      });
    },
    {
      enabled: !!legalEntityId && !!from && !!to,
    }
  );

// One request for the whole legal entity; the rows are split by nursing home.
export const useLegalEntityImmediateTransferFeesPhoenix = ({
  legalEntityId,
  from,
  to,
}: {
  legalEntityId: string | undefined;
  from: string | undefined;
  to: string | undefined;
}): UseQueryResult<GetImmediateTransferFeesResult, unknown> =>
  useApiQuery(
    useReportingPhoenixApi,
    ['GET_LEGAL_ENTITY_IMMEDIATE_TRANSFER_FEES', legalEntityId, from, to],
    (api) => {
      if (!legalEntityId || !from || !to) {
        throw new Error('Missing legal entity ID or date range');
      }
      return api.getImmediateTransferFees({
        params: {
          legal_entity_id: legalEntityId,
          period_start: from,
          period_end: to,
        },
      });
    },
    {
      enabled: !!legalEntityId && !!from && !!to,
    }
  );

// One request for the whole legal entity; the rows are split by nursing home.
export const useLegalEntityEarningsChangesPhoenix = ({
  legalEntityId,
  from,
  to,
}: {
  legalEntityId: string | undefined;
  from: string | undefined;
  to: string | undefined;
}): UseQueryResult<GetEarningsChangesResult, unknown> =>
  useApiQuery(
    useReportingPhoenixApi,
    ['GET_LEGAL_ENTITY_EARNINGS_CHANGES', legalEntityId, from, to],
    (api) => {
      if (!legalEntityId || !from || !to) {
        throw new Error('Missing legal entity ID or date range');
      }
      return api.getEarningsChanges({
        params: {
          legal_entity_id: legalEntityId,
          period_start: from,
          period_end: to,
        },
      });
    },
    {
      enabled: !!legalEntityId && !!from && !!to,
    }
  );
